import { useState } from 'react';
import { updateGame } from '../../services/content/gameService';
import type { Game, GameScenario, GameChoice } from '../../types/content/game';

type View = 'list' | 'scenario' | 'choice';

interface GameScenariosModalProps {
  game: Game;
  onClose: () => void;
  onSuccess: () => void;
}

const qualityLevels = ['Poor', 'Good', 'Excellent', 'Dangerous'];

export function GameScenariosModal({ game, onClose, onSuccess }: GameScenariosModalProps) {
  const [scenarios, setScenarios] = useState<GameScenario[]>(
    game.scenarios ? JSON.parse(JSON.stringify(game.scenarios)) : []
  );
  const [view, setView] = useState<View>('list');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Scenario detail state
  const [editingScenarioIndex, setEditingScenarioIndex] = useState<number | null>(null);
  const [scTitle, setScTitle] = useState('');
  const [scText, setScText] = useState('');
  const [scType, setScType] = useState('');
  const [scDayTime, setScDayTime] = useState('');
  const [scChoices, setScChoices] = useState<GameChoice[]>([]);

  // Choice detail state
  const [editingChoiceIndex, setEditingChoiceIndex] = useState<number | null>(null);
  const [chLetter, setChLetter] = useState('');
  const [chText, setChText] = useState('');
  const [chQuality, setChQuality] = useState('');
  const [chImpact, setChImpact] = useState('');
  const [chFeedbackTitle, setChFeedbackTitle] = useState('');
  const [chFeedbackText, setChFeedbackText] = useState('');

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
  const btnOutline = 'px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors';
  const btnPrimary = 'px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors';
  const btnSmall = 'text-xs px-2 py-1 rounded transition-colors';

  // -- Scenario helpers --
  function openScenario(index: number | null) {
    if (index !== null) {
      const s = scenarios[index];
      setScTitle(s.title);
      setScText(s.scenario_text);
      setScType(s.type);
      setScDayTime(s.day_time);
      setScChoices(JSON.parse(JSON.stringify(s.choices || [])));
    } else {
      setScTitle('');
      setScText('');
      setScType('');
      setScDayTime('');
      setScChoices([]);
    }
    setEditingScenarioIndex(index);
    setView('scenario');
  }

  function saveScenario() {
    if (!scTitle.trim()) return;
    const updated = [...scenarios];
    const item: GameScenario = {
      title: scTitle.trim(),
      scenario_number: editingScenarioIndex !== null ? scenarios[editingScenarioIndex].scenario_number : scenarios.length + 1,
      scenario_text: scText.trim(),
      type: scType.trim(),
      day_time: scDayTime.trim(),
      choices: scChoices,
    };
    if (editingScenarioIndex !== null) {
      updated[editingScenarioIndex] = item;
    } else {
      updated.push(item);
    }
    setScenarios(updated.map((s, i) => ({ ...s, scenario_number: i + 1 })));
    setView('list');
  }

  function deleteScenario(index: number) {
    setScenarios(scenarios.filter((_, i) => i !== index).map((s, i) => ({ ...s, scenario_number: i + 1 })));
  }

  // -- Choice helpers --
  function openChoice(index: number | null) {
    if (index !== null) {
      const c = scChoices[index];
      setChLetter(c.letter);
      setChText(c.choice_text);
      setChQuality(c.quality_level);
      setChImpact(String(c.impact_score));
      setChFeedbackTitle(c.feedback_title);
      setChFeedbackText(c.feedback_text);
    } else {
      const letters = ['A', 'B', 'C', 'D'];
      const used = scChoices.map((c) => c.letter);
      setChLetter(letters.find((l) => !used.includes(l)) || '');
      setChText('');
      setChQuality('');
      setChImpact('0');
      setChFeedbackTitle('');
      setChFeedbackText('');
    }
    setEditingChoiceIndex(index);
    setView('choice');
  }

  function saveChoice() {
    if (!chLetter.trim() || !chText.trim()) return;
    const choice: GameChoice = {
      letter: chLetter.trim(),
      choice_text: chText.trim(),
      quality_level: chQuality.trim(),
      impact_score: parseInt(chImpact, 10) || 0,
      feedback_title: chFeedbackTitle.trim(),
      feedback_text: chFeedbackText.trim(),
    };
    const updated = [...scChoices];
    if (editingChoiceIndex !== null) {
      updated[editingChoiceIndex] = choice;
    } else {
      updated.push(choice);
    }
    setScChoices(updated);
    setView('scenario');
  }

  function deleteChoice(index: number) {
    setScChoices(scChoices.filter((_, i) => i !== index));
  }

  async function handleSave() {
    setError('');
    setLoading(true);
    try {
      await updateGame(game.id, { ...game, scenarios });
      onSuccess();
    } catch {
      setError('Failed to update scenarios');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-lg w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">

        {/* -- Scenario List -- */}
        {view === 'list' && (
          <>
            <div className="px-6 pt-6 pb-4 overflow-y-auto flex-1">
              <h2 className="text-lg font-bold text-gray-900 mb-1">Scenarios</h2>
              <p className="text-sm text-gray-500 mb-4">{scenarios.length} scenario(s)</p>
              <div className="flex justify-end mb-2">
                <button type="button" onClick={() => openScenario(null)} className={`${btnSmall} text-blue-600 hover:bg-blue-50`}>+ Add Scenario</button>
              </div>
              {scenarios.length === 0 ? (
                <p className="text-sm text-gray-400">No scenarios yet</p>
              ) : (
                <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                  {scenarios.map((s, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-2">
                      <div className="min-w-0">
                        <span className="text-xs text-gray-400 mr-2">#{s.scenario_number}</span>
                        <span className="text-sm text-gray-700">{s.title}</span>
                        <span className="text-xs text-gray-400 ml-2">({s.choices?.length ?? 0} choices)</span>
                      </div>
                      <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                        <button type="button" onClick={() => openScenario(i)} className={`${btnSmall} text-gray-500 hover:bg-gray-100`}>Edit</button>
                        <button type="button" onClick={() => deleteScenario(i)} className={`${btnSmall} text-red-500 hover:bg-red-50`}>Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {error && <p className="text-red-600 text-sm mt-3">{error}</p>}
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button type="button" onClick={onClose} className={btnOutline}>Cancel</button>
              <button type="button" onClick={handleSave} disabled={loading} className={btnPrimary}>{loading ? 'Saving...' : 'Save Scenarios'}</button>
            </div>
          </>
        )}

        {/* -- Scenario Detail -- */}
        {view === 'scenario' && (
          <>
            <div className="px-6 pt-6 pb-4 overflow-y-auto flex-1">
              <h2 className="text-lg font-bold text-gray-900 mb-4">{editingScenarioIndex !== null ? 'Edit Scenario' : 'Add Scenario'}</h2>
              <div className="space-y-4">
                <div>
                  <label className={labelClass}>Title *</label>
                  <input type="text" value={scTitle} onChange={(e) => setScTitle(e.target.value)} className={inputClass} placeholder="Scenario title" />
                </div>
                <div>
                  <label className={labelClass}>Scenario Text</label>
                  <textarea value={scText} onChange={(e) => setScText(e.target.value)} className={inputClass} rows={3} placeholder="Describe the scenario..." />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Type</label>
                    <input type="text" value={scType} onChange={(e) => setScType(e.target.value)} className={inputClass} placeholder="e.g. dialogue" />
                  </div>
                  <div>
                    <label className={labelClass}>Day/Time</label>
                    <input type="text" value={scDayTime} onChange={(e) => setScDayTime(e.target.value)} className={inputClass} placeholder="e.g. Morning" />
                  </div>
                </div>

                {/* Choices */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className={labelClass}>Choices ({scChoices.length})</label>
                    <button type="button" onClick={() => openChoice(null)} className={`${btnSmall} text-blue-600 hover:bg-blue-50`}>+ Add Choice</button>
                  </div>
                  {scChoices.length === 0 ? (
                    <p className="text-sm text-gray-400">No choices yet</p>
                  ) : (
                    <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                      {scChoices.map((c, i) => (
                        <div key={i} className="flex items-center justify-between px-3 py-2">
                          <div className="min-w-0">
                            <span className="text-sm font-medium text-gray-700">{c.letter}.</span>
                            <span className="text-sm text-gray-600 ml-1 truncate">{c.choice_text}</span>
                            <span className={`text-xs ml-2 px-1.5 py-0.5 rounded ${
                              c.quality_level === 'Excellent' ? 'bg-green-100 text-green-700' :
                              c.quality_level === 'Good' ? 'bg-blue-100 text-blue-700' :
                              c.quality_level === 'Poor' ? 'bg-yellow-100 text-yellow-700' :
                              c.quality_level === 'Dangerous' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'
                            }`}>{c.quality_level}</span>
                          </div>
                          <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                            <button type="button" onClick={() => openChoice(i)} className={`${btnSmall} text-gray-500 hover:bg-gray-100`}>Edit</button>
                            <button type="button" onClick={() => deleteChoice(i)} className={`${btnSmall} text-red-500 hover:bg-red-50`}>Delete</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button type="button" onClick={() => setView('list')} className={btnOutline}>Back</button>
              <button type="button" onClick={saveScenario} disabled={!scTitle.trim()} className={btnPrimary}>Done</button>
            </div>
          </>
        )}

        {/* -- Choice Detail -- */}
        {view === 'choice' && (
          <>
            <div className="px-6 pt-6 pb-4 overflow-y-auto flex-1">
              <h2 className="text-lg font-bold text-gray-900 mb-4">{editingChoiceIndex !== null ? 'Edit Choice' : 'Add Choice'}</h2>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Letter *</label>
                    <input type="text" value={chLetter} onChange={(e) => setChLetter(e.target.value.toUpperCase())} className={inputClass} maxLength={1} placeholder="A" />
                  </div>
                  <div>
                    <label className={labelClass}>Quality Level</label>
                    <select value={chQuality} onChange={(e) => setChQuality(e.target.value)} className={inputClass}>
                      <option value="">Select...</option>
                      {qualityLevels.map((q) => <option key={q} value={q}>{q}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Choice Text *</label>
                  <textarea value={chText} onChange={(e) => setChText(e.target.value)} className={inputClass} rows={2} placeholder="What the player says/does" />
                </div>
                <div>
                  <label className={labelClass}>Impact Score</label>
                  <input type="number" value={chImpact} onChange={(e) => setChImpact(e.target.value)} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Feedback Title</label>
                  <input type="text" value={chFeedbackTitle} onChange={(e) => setChFeedbackTitle(e.target.value)} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Feedback Text</label>
                  <textarea value={chFeedbackText} onChange={(e) => setChFeedbackText(e.target.value)} className={inputClass} rows={2} />
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button type="button" onClick={() => setView('scenario')} className={btnOutline}>Back</button>
              <button type="button" onClick={saveChoice} disabled={!chLetter.trim() || !chText.trim()} className={btnPrimary}>Done</button>
            </div>
          </>
        )}

      </div>
    </div>
  );
}
